/**
 * Communicate copies reply priors only from parent.bundle_json
 * (copyReplyPriorPayload). When one received history is stored as more than
 * one inbound row, replyTo must be the inbound letter whose payloads cover
 * the opened letter.
 */

export type ReplyParentLetter = {
  textSegments?: ReadonlyArray<{ segmentId?: string | null }>;
  attachments?: ReadonlyArray<{ segmentId?: string | null }>;
};

export type ReplyParentMessage = {
  id: string;
  direction: string;
};

export function letterPayloadIds(letter: ReplyParentLetter | null | undefined): Set<string> {
  const ids = new Set<string>();
  for (const segment of letter?.textSegments ?? []) addId(ids, segment.segmentId);
  for (const attachment of letter?.attachments ?? []) addId(ids, attachment.segmentId);
  return ids;
}

export function letterPayloadCount(letter: ReplyParentLetter | null | undefined): number {
  return (letter?.textSegments?.length ?? 0) + (letter?.attachments?.length ?? 0);
}

/**
 * Parent messageId for a Svar. An outbound letter is answered as itself.
 * An inbound letter uses the inbound row that contains every payload of the
 * opened letter, preferring the one with the most payloads.
 */
export function replyParentMessageId(input: {
  openedId: string | null | undefined;
  messages: readonly ReplyParentMessage[];
  letters: ReadonlyMap<string, ReplyParentLetter | null | undefined>;
}): string | null {
  const openedId = input.openedId?.trim() || null;
  const opened = openedId
    ? input.messages.find((message) => message.id === openedId)
    : undefined;
  if (opened && opened.direction !== 'in') return opened.id;

  const inbound = input.messages.filter((message) => message.direction === 'in');
  const needed = openedId ? letterPayloadIds(input.letters.get(openedId)) : new Set<string>();
  let best: { id: string; count: number } | null = null;
  for (const message of inbound) {
    const letter = input.letters.get(message.id);
    if (!letter) continue;
    const ids = letterPayloadIds(letter);
    if (needed.size > 0 && [...needed].some((id) => !ids.has(id))) continue;
    const count = Math.max(ids.size, letterPayloadCount(letter));
    if (!best || count > best.count) best = { id: message.id, count };
  }
  return best?.id ?? openedId;
}

function addId(ids: Set<string>, value: string | null | undefined): void {
  const text = value?.trim() ?? '';
  if (text) ids.add(text);
}
